"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import posthog from "posthog-js";
import { getBuildId } from "@/lib/version";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isVersionMismatch: boolean;
  isReloading: boolean;
}

/**
 * Root Error Boundary Component
 * 
 * Catches errors in the React component tree and provides fallback UI.
 * 
 * IMPORTANT: This no longer auto-reloads on "could not find function" errors
 * because that causes infinite reload loops when a function genuinely doesn't
 * exist in deployment. Instead, it shows a user-friendly message with options
 * to retry or continue using the site.
 * 
 * Auto-reload ONLY happens when there's a genuine build version mismatch
 * (the stored build ID differs from the current one).
 */
export class ErrorBoundary extends Component<Props, State> {
  private reloadTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isVersionMismatch: false,
      isReloading: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      isVersionMismatch: false,
    };
  }

  /**
   * Check if this is a genuine version mismatch (build IDs differ)
   * vs. a function that simply doesn't exist.
   */
  private checkVersionMismatch(): boolean {
    try {
      const currentBuildId = getBuildId();
      const storedBuildId = localStorage.getItem("app_build_id");

      // Only auto-reload if build IDs actually differ
      if (storedBuildId && storedBuildId !== currentBuildId) {
        return true;
      }
    } catch {
      // localStorage not available
    }
    return false;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary] Caught error:", error);
    console.error("[ErrorBoundary] Build ID:", getBuildId());

    // Extract ConvexError payload so PostHog shows the real server-side message.
    // When Convex throws server-side, the client receives a ConvexError with:
    //   error.message = "[Request ID: abc123] Server Error" (generic)
    //   (error as any).data = the actual thrown message from the Convex function
    const convexData = (error as any).data;
    const requestIdMatch = error.message?.match(/\[Request ID: ([a-z0-9]+)\]/i);
    const convexRequestId = requestIdMatch?.[1];

    // Report to PostHog Error Tracking with full Convex context
    posthog.captureException(error, {
      source: "root_error_boundary",
      buildId: getBuildId(),
      isVersionMismatch: this.checkVersionMismatch(),
      componentStack: errorInfo.componentStack,
      // ConvexError extras — these make PostHog errors actually readable
      convex_error_data: convexData ? String(convexData) : undefined,
      convex_request_id: convexRequestId,
      convex_is_server_error: !!convexRequestId,
    });

    const errorMessage = error.message?.toLowerCase() || "";
    const isConvexFunctionError =
      errorMessage.includes("could not find public function") ||
      errorMessage.includes("could not find function") ||
      errorMessage.includes("function signature mismatch");

    // Only auto-reload if:
    // 1. It's a Convex function error AND
    // 2. The build ID has actually changed (genuine version mismatch)
    if (isConvexFunctionError && this.checkVersionMismatch()) {
      console.log("[ErrorBoundary] Genuine version mismatch detected — scheduling reload");
      this.setState({ isVersionMismatch: true });
      this.scheduleReload();
    } else if (isConvexFunctionError) {
      console.log("[ErrorBoundary] Convex function error but no version mismatch — showing recovery UI");
    }
  }

  scheduleReload() {
    if (this.reloadTimeout) return;

    this.setState({ isReloading: true });

    this.reloadTimeout = setTimeout(() => {
      console.log("[ErrorBoundary] Reloading page to fetch latest code");
      window.location.reload();
    }, 2000);
  }

  handleManualReload = () => {
    if (this.reloadTimeout) clearTimeout(this.reloadTimeout);
    window.location.reload();
  };

  /**
   * Reset the error boundary and let the app try to render normally.
   * This allows users to continue using parts of the site that work.
   */
  handleContinue = () => {
    if (this.reloadTimeout) clearTimeout(this.reloadTimeout);
    this.setState({
      hasError: false,
      error: null,
      isVersionMismatch: false,
      isReloading: false,
    });
  };

  componentWillUnmount() {
    if (this.reloadTimeout) clearTimeout(this.reloadTimeout);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Genuine version mismatch — show update message with auto-reload
    if (this.state.isVersionMismatch) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Updating to latest version...
            </h2>
            <p className="text-gray-600 mb-6">
              We&apos;re loading the latest updates. This will only take a moment.
            </p>
            {this.state.isReloading && (
              <button
                onClick={this.handleManualReload}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Reload Now
              </button>
            )}
            <p className="mt-6 text-xs text-gray-400">Build ID: {getBuildId()}</p>
          </div>
        </div>
      );
    }

    // Other errors — show recovery UI with "Continue to Site" option
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-6">
              A feature encountered an error, but you can continue using the site.
            </p>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <div className="mb-6 p-4 bg-red-50 rounded-md text-left">
                <p className="text-sm font-mono text-red-800 break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleManualReload}
                className="w-full px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleContinue}
                className="w-full px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Continue to Site
              </button>
            </div>

            <p className="mt-6 text-xs text-gray-400">Build ID: {getBuildId()}</p>
          </div>
        </div>
      </div>
    );
  }
}
